import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Activity,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  Globe,
  LayoutGrid,
  List,
  LockKeyhole,
  Pause,
  Play,
  RefreshCw,
  Search,
  ShieldAlert,
  ShieldCheck,
  Store,
  Users,
  Wrench,
  X,
} from "lucide-react";
import { adminApi } from "../../../api/adminApi";
import Modal from "../../../components/common/modals/Modal";
import ConfirmModal from "../../../components/common/modals/ConfirmModal";
import "./StoreOverview.css";

const PAGE_SIZE = 10;
const SETUP_STATUSES = [
  "Pending",
  "In Progress",
  "Ready",
  "Complete",
  "Failed",
  "On Hold",
];

const MODAL_TABS = [
  { id: "overview", label: "Overview" },
  { id: "billing", label: "Billing" },
  { id: "domain", label: "Domain & SSL" },
  { id: "users", label: "Users" },
  { id: "history", label: "Monitoring" },
];

const formatDate = (value) => {
  if (!value) return "Never";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Unavailable" : date.toLocaleString();
};

const toTime = (value) => {
  const time = value ? new Date(value).getTime() : NaN;
  return Number.isNaN(time) ? 0 : time;
};

const ACTIVITY_RANGES = {
  all: { label: "Any time", days: null },
  7: { label: "Last 7 days", days: 7 },
  30: { label: "Last 30 days", days: 30 },
  90: { label: "Last 90 days", days: 90 },
  never: { label: "No activity", days: null },
};

const SORTS = {
  createdDesc: "Created date (newest)",
  createdAsc: "Created date (oldest)",
  activityDesc: "Latest activity",
  activityAsc: "Oldest activity",
  nameAsc: "Name A–Z",
  nameDesc: "Name Z–A",
  usersDesc: "Most users",
};

const CERTIFICATE_FILTERS = {
  all: "All SSL certificates",
  expired: "Expired",
  expiring7: "Expiring within 7 days",
  expiring30: "Expiring within 30 days",
  expiring60: "Expiring within 60 days",
  expiring90: "Expiring within 90 days",
  valid: "Valid (over 90 days)",
  invalid: "Invalid",
  unknown: "Not checked",
};

const DEFAULT_FILTERS = {
  search: "",
  status: "all",
  setup: "all",
  plan: "all",
  subscription: "all",
  activity: "all",
  ssl: "all",
  sort: "createdDesc",
};

const storeName = (store) => store.businessName || store.tenantCode || "";
const storeDomain = (store) =>
  store.domain || (store.subdomain ? `${store.subdomain}.tooraflex.co.za` : "");

const normalizeDomain = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .replace(/\/$/, "");

const getMonitoringLabel = (monitoring) => {
  if (!monitoring) return "Awaiting check";
  if (monitoring.healthStatus === "up") return "Online";
  if (monitoring.healthStatus === "down") return "Down";
  return "Unknown";
};

const getHealthTone = (monitoring) => {
  if (monitoring?.healthStatus === "up") return "up";
  if (monitoring?.healthStatus === "down") return "down";
  return "unknown";
};

const getCertificateExpiry = (monitoring) => {
  if (!monitoring?.sslValidTo) return null;
  const expiry = new Date(monitoring.sslValidTo).getTime();
  return Number.isNaN(expiry) ? null : expiry;
};

const getCertificateDaysLeft = (monitoring) => {
  const expiry = getCertificateExpiry(monitoring);
  if (expiry === null) return null;
  return Math.ceil((expiry - Date.now()) / 86400000);
};

const isCertificateExpired = (monitoring) => {
  const expiry = getCertificateExpiry(monitoring);
  return expiry !== null && expiry <= Date.now();
};

const getCertificateDaysLabel = (monitoring) => {
  const expiry = getCertificateExpiry(monitoring);
  if (expiry === null) return "Days left unavailable";
  const difference = expiry - Date.now();
  if (difference <= 0) {
    const daysAgo = Math.max(1, Math.ceil(Math.abs(difference) / 86400000));
    return `Expired ${daysAgo} day${daysAgo === 1 ? "" : "s"} ago`;
  }
  const daysLeft = Math.ceil(difference / 86400000);
  return `${daysLeft} day${daysLeft === 1 ? "" : "s"} left`;
};

const getCertificateLabel = (monitoring) => {
  if (!monitoring) return "Not checked";
  if (isCertificateExpired(monitoring)) return "Expired";
  if (monitoring.sslValid !== true) {
    return monitoring.sslValid === false ? "Invalid" : "Unavailable";
  }
  const daysRemaining = getCertificateDaysLeft(monitoring);
  if (daysRemaining !== null && daysRemaining <= 30) return "Expiring soon";
  return "Valid";
};

const getCertificateTone = (monitoring) => {
  if (isCertificateExpired(monitoring) || monitoring?.sslValid === false)
    return "invalid";
  if (monitoring?.sslValid === true) {
    const daysLeft = getCertificateDaysLeft(monitoring);
    return daysLeft !== null && daysLeft <= 30 ? "warn" : "valid";
  }
  return "unknown";
};

const matchesCertificateFilter = (monitoring, filter) => {
  if (filter === "all") return true;
  if (filter === "unknown")
    return (
      !monitoring ||
      monitoring.sslValid === null ||
      monitoring.sslValid === undefined
    );
  if (filter === "expired") return isCertificateExpired(monitoring);
  if (filter === "invalid")
    return monitoring?.sslValid === false && !isCertificateExpired(monitoring);
  if (monitoring?.sslValid !== true || isCertificateExpired(monitoring))
    return false;

  const daysLeft = getCertificateDaysLeft(monitoring);
  if (daysLeft === null) return false;
  const threshold = Number(filter.replace("expiring", ""));
  if (filter === "valid") return daysLeft > 90;
  return daysLeft <= threshold;
};

const HealthPill = ({ monitoring }) => {
  const tone = getHealthTone(monitoring);
  return (
    <span className={`so-pill is-${tone}`}>
      {tone === "up" ? (
        <CheckCircle size={13} aria-hidden="true" />
      ) : (
        <CircleAlert size={13} aria-hidden="true" />
      )}
      {getMonitoringLabel(monitoring)}
    </span>
  );
};

const SslPill = ({ monitoring }) => {
  const tone = getCertificateTone(monitoring);
  return (
    <span className={`so-pill is-${tone}`}>
      {tone === "valid" ? (
        <ShieldCheck size={13} aria-hidden="true" />
      ) : (
        <ShieldAlert size={13} aria-hidden="true" />
      )}
      SSL {getCertificateLabel(monitoring).toLowerCase()}
    </span>
  );
};

const Avatar = ({ store, large = false }) => (
  <span className={`so-avatar${large ? " is-large" : ""}`} aria-hidden="true">
    {(storeName(store)[0] || "?").toUpperCase()}
  </span>
);

const StatusBadge = ({
  active,
  activeLabel = "Active",
  inactiveLabel = "Inactive",
}) => (
  <span className={`so-status ${active ? "is-active" : "is-inactive"}`}>
    {active ? activeLabel : inactiveLabel}
  </span>
);

const StoreOverview = () => {
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState("table");
  const [activeTab, setActiveTab] = useState("overview");
  const [setupStatusDraft, setSetupStatusDraft] = useState("");
  const [savingSetupStatus, setSavingSetupStatus] = useState(false);
  const [plans, setPlans] = useState([]);
  const [planDraft, setPlanDraft] = useState("");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("EFT");
  const [paymentReference, setPaymentReference] = useState("");
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const [savingPlan, setSavingPlan] = useState(false);
  const [updatingStoreStatus, setUpdatingStoreStatus] = useState(false);
  const [domainDraft, setDomainDraft] = useState("");
  const [dnsCheck, setDnsCheck] = useState(null);
  const [checkingDomainDns, setCheckingDomainDns] = useState(false);
  const [savingDomain, setSavingDomain] = useState(false);
  const [savingMaintenance, setSavingMaintenance] = useState(false);
  const [updatingUserId, setUpdatingUserId] = useState("");
  const [checkingHealth, setCheckingHealth] = useState(false);
  const [checkingAllStores, setCheckingAllStores] = useState(false);
  const [renewingCertificate, setRenewingCertificate] = useState(false);
  const [detailsError, setDetailsError] = useState("");
  const [detailsMessage, setDetailsMessage] = useState("");
  const [healthCheckSummary, setHealthCheckSummary] = useState("");
  const [pendingConfirmation, setPendingConfirmation] = useState(null);

  const loadStores = useCallback(async (silent = false) => {
    try {
      silent ? setRefreshing(true) : setLoading(true);
      setError("");
      const response = await adminApi.getStoreOverview();
      const data = response.data;
      if (!Array.isArray(data)) {
        throw new Error("The store overview response was invalid.");
      }
      setStores(data);
      setSelectedStore((current) =>
        current
          ? data.find(
              (store) =>
                String(store.tenantId).toLowerCase() ===
                String(current.tenantId).toLowerCase(),
            ) || null
          : null,
      );
    } catch (loadError) {
      setError(
        loadError.response?.data?.error ||
          loadError.message ||
          "Could not load the store overview.",
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadStores();
  }, [loadStores]);

  useEffect(() => {
    const refreshInterval = window.setInterval(
      () => loadStores(true),
      5 * 60 * 1000,
    );
    return () => window.clearInterval(refreshInterval);
  }, [loadStores]);

  const setFilter = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
    setPage(1);
  };

  const resetFilters = () => {
    setFilters((current) => ({ ...DEFAULT_FILTERS, sort: current.sort }));
    setPage(1);
  };

  const options = useMemo(() => {
    const unique = (key) =>
      [...new Set(stores.map((s) => s[key]).filter(Boolean))].sort();
    return {
      setup: unique("setupStatus"),
      plan: unique("subscriptionPlan"),
      subscription: unique("subscriptionStatus"),
    };
  }, [stores]);

  const filteredStores = useMemo(() => {
    const query = filters.search.trim().toLowerCase();
    const range = ACTIVITY_RANGES[filters.activity];
    const cutoff = range?.days ? Date.now() - range.days * 86400000 : null;

    const result = stores.filter((store) => {
      if (query) {
        const haystack = [
          store.businessName,
          store.tenantCode,
          storeDomain(store),
          store.businessEmail,
          ...(Array.isArray(store.users)
            ? store.users.flatMap((u) => [u.userName, u.email])
            : []),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(query)) return false;
      }
      if (filters.status === "active" && !store.isActive) return false;
      if (filters.status === "inactive" && store.isActive) return false;
      if (filters.setup !== "all" && store.setupStatus !== filters.setup)
        return false;
      if (filters.plan !== "all" && store.subscriptionPlan !== filters.plan)
        return false;
      if (
        filters.subscription !== "all" &&
        store.subscriptionStatus !== filters.subscription
      )
        return false;
      if (!matchesCertificateFilter(store.monitoring, filters.ssl))
        return false;
      if (filters.activity === "never" && store.lastActivityAt) return false;
      if (cutoff && toTime(store.lastActivityAt) < cutoff) return false;
      return true;
    });

    const userTotal = (s) => (Array.isArray(s.users) ? s.users.length : 0);
    const sorters = {
      createdDesc: (a, b) => toTime(b.createdAt) - toTime(a.createdAt),
      createdAsc: (a, b) => toTime(a.createdAt) - toTime(b.createdAt),
      activityDesc: (a, b) =>
        toTime(b.lastActivityAt) - toTime(a.lastActivityAt),
      activityAsc: (a, b) =>
        toTime(a.lastActivityAt) - toTime(b.lastActivityAt),
      nameAsc: (a, b) => storeName(a).localeCompare(storeName(b)),
      nameDesc: (a, b) => storeName(b).localeCompare(storeName(a)),
      usersDesc: (a, b) => userTotal(b) - userTotal(a),
    };
    return [...result].sort(sorters[filters.sort]);
  }, [stores, filters]);

  const activeStoreCount = useMemo(
    () => stores.filter((store) => store.isActive).length,
    [stores],
  );
  const userCount = useMemo(
    () =>
      stores.reduce(
        (total, store) =>
          total + (Array.isArray(store.users) ? store.users.length : 0),
        0,
      ),
    [stores],
  );
  const onlineStoreCount = useMemo(
    () =>
      stores.filter((store) => store.monitoring?.healthStatus === "up").length,
    [stores],
  );
  const sslExpiredCount = useMemo(
    () =>
      stores.filter((store) => isCertificateExpired(store.monitoring)).length,
    [stores],
  );
  const sslExpiringCount = useMemo(
    () =>
      stores.filter(
        (store) =>
          store.monitoring?.sslValid === true &&
          !isCertificateExpired(store.monitoring) &&
          (getCertificateDaysLeft(store.monitoring) ?? Infinity) <= 30,
      ).length,
    [stores],
  );
  const sslAlertCount = useMemo(
    () =>
      stores.filter(
        (store) =>
          store.monitoring?.sslValid === false ||
          isCertificateExpired(store.monitoring) ||
          (getCertificateDaysLeft(store.monitoring) ?? Infinity) <= 30,
      ).length,
    [stores],
  );

  const totalPages = Math.max(1, Math.ceil(filteredStores.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const pageStores = filteredStores.slice(pageStart, pageStart + PAGE_SIZE);

  const activeFilterCount = Object.keys(DEFAULT_FILTERS).filter(
    (key) => key !== "sort" && filters[key] !== DEFAULT_FILTERS[key],
  ).length;

  const selectedUsers = Array.isArray(selectedStore?.users)
    ? selectedStore.users
    : [];

  const openStore = async (store) => {
    setSelectedStore(store);
    setActiveTab("overview");
    setSetupStatusDraft(store.setupStatus || SETUP_STATUSES[0]);
    setDomainDraft(store.domain || storeDomain(store));
    setDnsCheck(null);
    setPlanDraft("");
    setPaymentAmount("");
    setPaymentReference("");
    setPaymentConfirmed(false);
    setDetailsError("");
    setDetailsMessage("");
    try {
      const response = await adminApi.getStorePlans();
      const availablePlans = Array.isArray(response.data) ? response.data : [];
      setPlans(availablePlans);
      const currentPlan = availablePlans.find(
        (plan) =>
          String(plan.Name || "").toLowerCase() ===
            String(store.subscriptionPlan || "").toLowerCase() ||
          String(plan.Tier || "").toLowerCase() ===
            String(store.subscriptionPlan || "").toLowerCase(),
      );
      setPlanDraft(currentPlan?.PackageID || "");
    } catch (loadError) {
      setDetailsError(
        loadError.response?.data?.error ||
          loadError.message ||
          "Could not load subscription plans.",
      );
    }
  };

  const closeStore = () => {
    setSelectedStore(null);
    setDetailsError("");
    setDetailsMessage("");
  };

  const updateStoreInView = (tenantId, changes) => {
    const matchesTenant = (store) =>
      String(store.tenantId).toLowerCase() === String(tenantId).toLowerCase();
    setStores((current) =>
      current.map((store) =>
        matchesTenant(store) ? { ...store, ...changes } : store,
      ),
    );
    setSelectedStore((current) =>
      current && matchesTenant(current) ? { ...current, ...changes } : current,
    );
  };

  const withMonitoringHistory = (store, monitoring) => ({
    ...store,
    monitoring,
    monitoringHistory: [
      monitoring,
      ...(store.monitoringHistory || []).filter(
        (check) => check.monitoringCheckId !== monitoring.monitoringCheckId,
      ),
    ].slice(0, 10),
  });

  const applyMonitoring = (tenantId, monitoring) => {
    const matchesTenant = (store) =>
      String(store.tenantId).toLowerCase() === String(tenantId).toLowerCase();
    setStores((current) =>
      current.map((store) =>
        matchesTenant(store) ? withMonitoringHistory(store, monitoring) : store,
      ),
    );
    setSelectedStore((current) =>
      current && matchesTenant(current)
        ? withMonitoringHistory(current, monitoring)
        : current,
    );
  };

  const savePlanChange = async () => {
    if (!selectedStore || savingPlan) return;
    if (!paymentConfirmed) {
      setDetailsError(
        "Confirm that you verified the payment before changing the plan.",
      );
      return;
    }
    try {
      setSavingPlan(true);
      setDetailsError("");
      setDetailsMessage("");
      const response = await adminApi.updateStorePlan(selectedStore.tenantId, {
        packageId: planDraft,
        amount: paymentAmount,
        paymentMethod,
        paymentReference,
        paymentConfirmed,
      });
      const updated = response.data;
      updateStoreInView(selectedStore.tenantId, {
        subscriptionPlan: updated.subscriptionPlan,
      });
      setDetailsMessage(
        `Payment ${updated.paymentReference} recorded as completed; plan changed to ${updated.subscriptionPlan}.`,
      );
      setPaymentReference("");
      setPaymentConfirmed(false);
    } catch (saveError) {
      setDetailsError(
        saveError.response?.data?.error ||
          saveError.message ||
          "Could not change the subscription plan.",
      );
    } finally {
      setSavingPlan(false);
    }
  };

  const updateStoreStatus = async (store, nextIsActive) => {
    try {
      setUpdatingStoreStatus(true);
      setDetailsError("");
      setDetailsMessage("");
      const response = await adminApi.updateStoreStatus(
        store.tenantId,
        nextIsActive,
      );
      updateStoreInView(store.tenantId, response.data);
      setDetailsMessage(
        nextIsActive
          ? "Store reactivated. Existing tenant data was preserved."
          : "Store suspended. Existing tenant data was preserved.",
      );
    } catch (statusError) {
      setDetailsError(
        statusError.response?.data?.error ||
          statusError.message ||
          "Could not update store status.",
      );
    } finally {
      setUpdatingStoreStatus(false);
    }
  };

  const toggleStoreStatus = () => {
    if (!selectedStore || updatingStoreStatus) return;
    const store = selectedStore;
    const nextIsActive = !store.isActive;
    setPendingConfirmation({
      title: nextIsActive ? "Reactivate storefront" : "Suspend storefront",
      message: nextIsActive
        ? `Reactivate ${storeName(store)}? The storefront will become available again.`
        : `Suspend ${storeName(store)}? Storefront visitors will be blocked, but store data and accounts will be preserved.`,
      confirmText: nextIsActive ? "Reactivate store" : "Suspend store",
      variant: nextIsActive ? "primary" : "danger",
      onConfirm: () => updateStoreStatus(store, nextIsActive),
    });
  };

  const checkDomainDns = async () => {
    if (!selectedStore || checkingDomainDns) return;
    try {
      setCheckingDomainDns(true);
      setDetailsError("");
      setDetailsMessage("");
      setDnsCheck(null);
      const response = await adminApi.verifyStoreDomainDns(
        selectedStore.tenantId,
        domainDraft,
      );
      setDnsCheck(response.data);
      if (!response.data.verified) {
        setDetailsError(
          response.data.message || "The domain DNS check failed.",
        );
      }
    } catch (checkError) {
      setDetailsError(
        checkError.response?.data?.error ||
          checkError.message ||
          "Could not verify the domain DNS.",
      );
    } finally {
      setCheckingDomainDns(false);
    }
  };

  const normalizedDomainDraft = normalizeDomain(domainDraft);
  const domainReadyToSave =
    Boolean(dnsCheck?.verified) && dnsCheck?.domain === normalizedDomainDraft;

  const saveStoreDomain = async () => {
    if (!selectedStore || savingDomain) return;
    if (!domainReadyToSave) {
      setDetailsError(
        "Verify public DNS for this exact domain before updating it.",
      );
      return;
    }
    try {
      setSavingDomain(true);
      setDetailsError("");
      setDetailsMessage("");
      const response = await adminApi.updateStoreDomain(
        selectedStore.tenantId,
        domainDraft,
      );
      const updated = response.data;
      updateStoreInView(selectedStore.tenantId, {
        domain: updated.domain,
        monitoring: null,
        monitoringHistory: [],
      });
      setDomainDraft(updated.domain);
      setDnsCheck(null);
      setDetailsMessage(
        `Domain updated to ${updated.domain}; its Nginx site and SSL certificate were configured.`,
      );
    } catch (domainError) {
      setDetailsError(
        domainError.response?.data?.error ||
          domainError.message ||
          "Could not update the store domain.",
      );
    } finally {
      setSavingDomain(false);
    }
  };

  const updateMaintenanceMode = async (store, enabled) => {
    try {
      setSavingMaintenance(true);
      setDetailsError("");
      setDetailsMessage("");
      const response = await adminApi.updateStoreMaintenanceMode(
        store.tenantId,
        enabled,
      );
      updateStoreInView(store.tenantId, response.data);
      setDetailsMessage(
        enabled
          ? "Maintenance mode is on; visitors receive an HTTP 503 response."
          : "Maintenance mode is off; the storefront is available again.",
      );
    } catch (maintenanceError) {
      setDetailsError(
        maintenanceError.response?.data?.error ||
          maintenanceError.message ||
          "Could not update maintenance mode.",
      );
    } finally {
      setSavingMaintenance(false);
    }
  };

  const toggleMaintenanceMode = () => {
    if (!selectedStore || savingMaintenance) return;
    const store = selectedStore;
    const enabled = !store.maintenanceMode;
    setPendingConfirmation({
      title: enabled ? "Enable maintenance mode" : "Disable maintenance mode",
      message: enabled
        ? `Enable maintenance mode for ${storeName(store)}? Visitors will receive an HTTP 503 response until maintenance is disabled.`
        : `Disable maintenance mode for ${storeName(store)}? Visitors will be able to access the storefront again.`,
      confirmText: enabled ? "Enable maintenance" : "Disable maintenance",
      variant: enabled ? "warning" : "primary",
      onConfirm: () => updateMaintenanceMode(store, enabled),
    });
  };

  const confirmPendingAction = async () => {
    if (!pendingConfirmation) return;
    const { onConfirm } = pendingConfirmation;
    setPendingConfirmation(null);
    await onConfirm();
  };

  const toggleTenantUserAccess = async (user) => {
    if (!selectedStore || updatingUserId) return;
    const isActive = !user.isActive;
    try {
      setUpdatingUserId(user.userId);
      setDetailsError("");
      setDetailsMessage("");
      const response = await adminApi.updateStoreUserAccess(
        selectedStore.tenantId,
        user.userId,
        isActive,
      );
      const updatedUser = response.data;
      const users = selectedStore.users.map((current) =>
        String(current.userId).toLowerCase() ===
        String(updatedUser.userId).toLowerCase()
          ? { ...current, isActive: updatedUser.isActive }
          : current,
      );
      updateStoreInView(selectedStore.tenantId, { users });
      setDetailsMessage(
        `${updatedUser.email || updatedUser.userName || "Account"} access ${
          isActive ? "enabled" : "disabled"
        }.`,
      );
    } catch (accessError) {
      setDetailsError(
        accessError.response?.data?.error ||
          accessError.message ||
          "Could not update account access.",
      );
    } finally {
      setUpdatingUserId("");
    }
  };

  const saveSetupStatus = async () => {
    if (!selectedStore || savingSetupStatus) return;
    try {
      setSavingSetupStatus(true);
      setDetailsError("");
      setDetailsMessage("");
      const response = await adminApi.updateStoreSetupStatus(
        selectedStore.tenantId,
        setupStatusDraft,
      );
      const updated = response.data;
      updateStoreInView(updated.tenantId, {
        setupStatus: updated.setupStatus,
        setupCompletedAt: updated.setupCompletedAt,
      });
      setDetailsMessage(`Setup status changed to ${updated.setupStatus}.`);
    } catch (saveError) {
      setDetailsError(
        saveError.response?.data?.error ||
          saveError.message ||
          "Could not update the setup status.",
      );
    } finally {
      setSavingSetupStatus(false);
    }
  };

  const checkStoreHealth = async () => {
    if (!selectedStore || checkingHealth) return;
    try {
      setCheckingHealth(true);
      setDetailsError("");
      const response = await adminApi.checkStoreHealth(selectedStore.tenantId);
      applyMonitoring(selectedStore.tenantId, response.data);
    } catch (checkError) {
      setDetailsError(
        checkError.response?.data?.error ||
          checkError.message ||
          "Could not check store health.",
      );
    } finally {
      setCheckingHealth(false);
    }
  };

  const checkAllStoreHealth = async () => {
    if (checkingAllStores || refreshing) return;
    try {
      setCheckingAllStores(true);
      setError("");
      setHealthCheckSummary("");
      const response = await adminApi.checkAllStoreHealth();
      const summary = response.data;
      await loadStores(true);
      setHealthCheckSummary(
        `${summary.checked} of ${summary.total} stores checked${
          summary.failed ? `; ${summary.failed} checks could not be saved` : ""
        }.`,
      );
    } catch (checkError) {
      setError(
        checkError.response?.data?.error ||
          checkError.message ||
          "Could not check all store health.",
      );
    } finally {
      setCheckingAllStores(false);
    }
  };

  const renewStoreCertificate = async () => {
    if (!selectedStore || renewingCertificate) return;
    try {
      setRenewingCertificate(true);
      setDetailsError("");
      setDetailsMessage("");
      const response = await adminApi.renewStoreCertificate(
        selectedStore.tenantId,
      );
      const result = response.data;
      if (result.monitoring) {
        applyMonitoring(selectedStore.tenantId, result.monitoring);
      }
      setDetailsMessage(
        [result.message, result.output, result.monitoringWarning]
          .filter(Boolean)
          .join(" "),
      );
    } catch (renewError) {
      setDetailsError(
        renewError.response?.data?.error ||
          renewError.message ||
          "Could not renew the store SSL certificate.",
      );
    } finally {
      setRenewingCertificate(false);
    }
  };

  const handleStoreRowKeyDown = (event, store) => {
    if (
      event.target === event.currentTarget &&
      (event.key === "Enter" || event.key === " ")
    ) {
      event.preventDefault();
      openStore(store);
    }
  };

  if (loading) {
    return (
      <div className="store-overview-page" role="status">
        <div className="spinner" />
        <p>Loading stores...</p>
      </div>
    );
  }

  const monitoring = selectedStore?.monitoring;
  const selectedDomain = selectedStore ? storeDomain(selectedStore) : "";

  const renderOverviewTab = () => (
    <div className="so-tab-panel">
      <section className="so-section">
        <h3>Store information</h3>
        <dl className="so-info-grid">
          <div>
            <dt>Domain</dt>
            <dd>
              {monitoring?.domain ? (
                <a
                  href={`https://${monitoring.domain}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {monitoring.domain}
                </a>
              ) : (
                selectedDomain || "No domain configured"
              )}
            </dd>
          </div>
          <div>
            <dt>Tenant code</dt>
            <dd>{selectedStore.tenantCode || "—"}</dd>
          </div>
          <div>
            <dt>Subscription</dt>
            <dd>
              {selectedStore.subscriptionPlan || "—"}
              <span className="so-sub">
                {selectedStore.subscriptionStatus || "Unknown"}
              </span>
            </dd>
          </div>
          <div>
            <dt>Business contact</dt>
            <dd>
              {selectedStore.businessEmail || "—"}
              {selectedStore.businessPhone && (
                <span className="so-sub">{selectedStore.businessPhone}</span>
              )}
            </dd>
          </div>
          <div>
            <dt>Created</dt>
            <dd>{formatDate(selectedStore.createdAt)}</dd>
          </div>
          <div>
            <dt>Last activity</dt>
            <dd>{formatDate(selectedStore.lastActivityAt)}</dd>
          </div>
          <div className="is-wide">
            <dt>Tenant ID</dt>
            <dd className="so-mono">{selectedStore.tenantId}</dd>
          </div>
        </dl>
      </section>

      <div className="so-card-pair">
        <section className="so-section">
          <div className="so-section-head">
            <h3>Store health</h3>
            <button
              type="button"
              className="so-btn so-btn-sm"
              onClick={checkStoreHealth}
              disabled={checkingHealth}
            >
              <RefreshCw
                size={14}
                className={checkingHealth ? "spin" : ""}
                aria-hidden="true"
              />
              {checkingHealth ? "Checking..." : "Check now"}
            </button>
          </div>
          <div className="so-metric-head">
            <HealthPill monitoring={monitoring} />
          </div>
          <dl className="so-info-list">
            <div>
              <dt>HTTP status</dt>
              <dd>{monitoring?.httpStatus || "—"}</dd>
            </div>
            <div>
              <dt>Response time</dt>
              <dd>
                {monitoring?.responseTimeMs !== null &&
                monitoring?.responseTimeMs !== undefined
                  ? `${monitoring.responseTimeMs} ms`
                  : "—"}
              </dd>
            </div>
            <div>
              <dt>Last checked</dt>
              <dd>{formatDate(monitoring?.checkedAt)}</dd>
            </div>
          </dl>
          {monitoring?.errorMessage && (
            <p className="so-inline-error">{monitoring.errorMessage}</p>
          )}
        </section>

        <section className="so-section">
          <div className="so-section-head">
            <h3>SSL certificate</h3>
            <button
              type="button"
              className="so-btn so-btn-sm"
              onClick={renewStoreCertificate}
              disabled={renewingCertificate || !selectedDomain}
            >
              <RefreshCw
                size={14}
                className={renewingCertificate ? "spin" : ""}
                aria-hidden="true"
              />
              {renewingCertificate ? "Renewing..." : "Renew"}
            </button>
          </div>
          <div className="so-metric-head">
            <SslPill monitoring={monitoring} />
            {monitoring?.sslValidTo && (
              <span
                className={`so-metric-note is-${getCertificateTone(monitoring)}`}
              >
                {getCertificateDaysLabel(monitoring)}
              </span>
            )}
          </div>
          <dl className="so-info-list">
            <div>
              <dt>Expires</dt>
              <dd>
                {monitoring?.sslValidTo
                  ? formatDate(monitoring.sslValidTo)
                  : "—"}
              </dd>
            </div>
            <div>
              <dt>Subject</dt>
              <dd>{monitoring?.sslSubject || "—"}</dd>
            </div>
            <div>
              <dt>Issuer</dt>
              <dd>{monitoring?.sslIssuer || "—"}</dd>
            </div>
          </dl>
          {monitoring?.sslError && (
            <p className="so-inline-error">{monitoring.sslError}</p>
          )}
        </section>
      </div>

      <section className="so-section">
        <h3>Store controls</h3>
        <div className="so-control-rows">
          <div className="so-control-row">
            <div className="so-control-text">
              <strong>Tenant status</strong>
              <span>
                Suspending blocks access. Store data is never deleted.
              </span>
            </div>
            <div className="so-control-action">
              <StatusBadge active={selectedStore.isActive} />
              <button
                type="button"
                className="so-btn"
                onClick={toggleStoreStatus}
                disabled={updatingStoreStatus}
              >
                {selectedStore.isActive ? (
                  <Pause size={14} aria-hidden="true" />
                ) : (
                  <Play size={14} aria-hidden="true" />
                )}
                {updatingStoreStatus
                  ? "Updating..."
                  : selectedStore.isActive
                    ? "Suspend"
                    : "Reactivate"}
              </button>
            </div>
          </div>

          <div className="so-control-row">
            <div className="so-control-text">
              <strong>Setup status</strong>
              <span>Tracks onboarding progress for this store.</span>
            </div>
            <div className="so-control-action">
              <select
                value={setupStatusDraft}
                onChange={(event) => setSetupStatusDraft(event.target.value)}
                aria-label="Store setup status"
                disabled={savingSetupStatus}
              >
                {[
                  ...new Set([
                    ...SETUP_STATUSES,
                    ...(selectedStore.setupStatus
                      ? [selectedStore.setupStatus]
                      : []),
                  ]),
                ].map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="so-btn so-btn-primary"
                onClick={saveSetupStatus}
                disabled={
                  savingSetupStatus ||
                  setupStatusDraft === selectedStore.setupStatus
                }
              >
                {savingSetupStatus ? "Saving..." : "Save"}
              </button>
            </div>
          </div>

          <div className="so-control-row">
            <div className="so-control-text">
              <strong>Maintenance mode</strong>
              <span>
                Visitors receive HTTP 503 while maintenance is enabled. Admin
                controls stay available.
              </span>
            </div>
            <div className="so-control-action">
              <StatusBadge
                active={!selectedStore.maintenanceMode}
                activeLabel="Disabled"
                inactiveLabel="Enabled"
              />
              <button
                type="button"
                className={
                  selectedStore.maintenanceMode
                    ? "so-btn"
                    : "so-btn so-btn-primary"
                }
                onClick={toggleMaintenanceMode}
                disabled={savingMaintenance}
              >
                <Wrench size={14} aria-hidden="true" />
                {savingMaintenance
                  ? "Updating..."
                  : selectedStore.maintenanceMode
                    ? "Disable"
                    : "Enable"}
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );

  const renderBillingTab = () => (
    <div className="so-tab-panel">
      <section className="so-section">
        <div className="so-section-intro">
          <h3>Subscription and billing</h3>
          <p>
            Verify that payment has been received before applying a plan change.
            The payment reference and administrator are recorded.
          </p>
        </div>
        <div className="so-form-grid">
          <label className="so-field is-wide">
            <span>New plan</span>
            <select
              value={planDraft}
              onChange={(event) => {
                const plan = plans.find(
                  (item) => item.PackageID === event.target.value,
                );
                setPlanDraft(event.target.value);
                setPaymentAmount(
                  plan?.Price === null || plan?.Price === undefined
                    ? ""
                    : Number(plan.Price).toFixed(2),
                );
              }}
              disabled={savingPlan || plans.length === 0}
            >
              <option value="">Select a plan</option>
              {plans.map((plan) => (
                <option key={plan.PackageID} value={plan.PackageID}>
                  {plan.Name} –{" "}
                  {plan.Price === null || plan.Price === undefined
                    ? "Quote required"
                    : `${plan.Currency || "ZAR"} ${Number(plan.Price).toFixed(2)}`}
                  {plan.BillingPeriod ? ` / ${plan.BillingPeriod}` : ""}
                </option>
              ))}
            </select>
          </label>
          <label className="so-field">
            <span>Amount received</span>
            <input
              type="number"
              min="0.01"
              max="99999999.99"
              step="0.01"
              value={paymentAmount}
              onChange={(event) => setPaymentAmount(event.target.value)}
              disabled={savingPlan}
            />
          </label>
          <label className="so-field">
            <span>Payment method</span>
            <select
              value={paymentMethod}
              onChange={(event) => setPaymentMethod(event.target.value)}
              disabled={savingPlan}
            >
              <option value="EFT">EFT / bank transfer</option>
              <option value="PayFast">PayFast</option>
              <option value="Ozow">Ozow</option>
              <option value="Other">Other</option>
            </select>
          </label>
          <label className="so-field is-wide">
            <span>Payment reference</span>
            <input
              type="text"
              maxLength={100}
              value={paymentReference}
              onChange={(event) => setPaymentReference(event.target.value)}
              disabled={savingPlan}
              placeholder="Bank or transaction reference"
            />
          </label>
        </div>
        <label className="so-confirmation">
          <input
            type="checkbox"
            checked={paymentConfirmed}
            onChange={(event) => setPaymentConfirmed(event.target.checked)}
            disabled={savingPlan}
          />
          <span>I have verified that this payment was received.</span>
        </label>
        <div className="so-form-actions">
          <button
            type="button"
            className="so-btn so-btn-primary"
            onClick={savePlanChange}
            disabled={
              savingPlan ||
              !planDraft ||
              !paymentAmount ||
              !paymentReference.trim() ||
              !paymentConfirmed
            }
          >
            {savingPlan
              ? "Recording payment..."
              : "Record payment and change plan"}
          </button>
        </div>
      </section>
    </div>
  );

  const renderDomainTab = () => (
    <div className="so-tab-panel">
      <section className="so-section">
        <div className="so-section-intro">
          <h3>Domain and SSL</h3>
          <p>
            DNS must resolve publicly for both the domain and www before the
            Nginx site and certificate are updated.
          </p>
        </div>
        <label className="so-field">
          <span>Store domain</span>
          <input
            type="text"
            value={domainDraft}
            onChange={(event) => {
              setDomainDraft(event.target.value);
              setDnsCheck(null);
            }}
            disabled={checkingDomainDns || savingDomain}
            placeholder="shop.example.com"
          />
        </label>
        {dnsCheck && (
          <div
            className={`so-domain-check ${
              dnsCheck.verified ? "is-valid" : "is-invalid"
            }`}
            role="status"
          >
            <strong>{dnsCheck.message}</strong>
            {dnsCheck.checks?.map((check) => (
              <span key={check.hostname}>
                <b>{check.hostname}</b>
                {check.verified
                  ? check.addresses.map((address) => address.address).join(", ")
                  : check.error}
              </span>
            ))}
          </div>
        )}
        <div className="so-form-actions">
          <button
            type="button"
            className="so-btn"
            onClick={checkDomainDns}
            disabled={checkingDomainDns || savingDomain || !domainDraft.trim()}
          >
            <Globe size={14} aria-hidden="true" />
            {checkingDomainDns ? "Checking DNS..." : "Verify DNS"}
          </button>
          <button
            type="button"
            className="so-btn so-btn-primary"
            onClick={saveStoreDomain}
            disabled={savingDomain || !domainReadyToSave}
          >
            {savingDomain
              ? "Configuring domain and SSL..."
              : "Update domain and configure SSL"}
          </button>
        </div>
      </section>
    </div>
  );

  const renderUsersTab = () => (
    <div className="so-tab-panel">
      <section className="so-section">
        <h3>Tenant users ({selectedUsers.length})</h3>
        {selectedUsers.length === 0 ? (
          <p className="so-muted-text">
            No users are associated with this tenant.
          </p>
        ) : (
          <ul className="so-user-list">
            {selectedUsers.map((user) => (
              <li key={user.userId} className="so-user-row">
                <div className="so-user-main">
                  <strong>{user.userName || "Unnamed user"}</strong>
                  <span>{user.email || "No email"}</span>
                  {user.role && <em>{user.role}</em>}
                </div>
                <dl className="so-user-meta">
                  <div>
                    <dt>Last login</dt>
                    <dd>{formatDate(user.lastLogin)}</dd>
                  </div>
                  <div>
                    <dt>Last session</dt>
                    <dd>{formatDate(user.lastSessionActivity)}</dd>
                  </div>
                </dl>
                <div className="so-user-actions">
                  <StatusBadge active={user.isActive} />
                  <button
                    type="button"
                    className="so-btn so-btn-sm"
                    onClick={() => toggleTenantUserAccess(user)}
                    disabled={updatingUserId === user.userId}
                  >
                    {updatingUserId === user.userId
                      ? "Updating..."
                      : user.isActive
                        ? "Disable access"
                        : "Enable access"}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );

  const renderHistoryTab = () => (
    <div className="so-tab-panel">
      <section className="so-section">
        <h3>Monitoring history (last 10 checks)</h3>
        {selectedStore.monitoringHistory?.length ? (
          <ul className="so-history-list">
            {selectedStore.monitoringHistory.map((check) => (
              <li key={check.monitoringCheckId}>
                <HealthPill monitoring={check} />
                <span className="so-history-domain">
                  {check.domain || "No domain"}
                </span>
                <span className="so-history-detail">
                  {check.httpStatus
                    ? `HTTP ${check.httpStatus}`
                    : check.errorMessage || "No response"}
                </span>
                <time className="so-history-time">
                  {formatDate(check.checkedAt)}
                </time>
              </li>
            ))}
          </ul>
        ) : (
          <p className="so-muted-text">
            No monitoring checks have been recorded yet.
          </p>
        )}
      </section>
    </div>
  );

  return (
    <div className="store-overview-page">
      <header className="store-overview-header">
        <div>
          <h2>Store overview</h2>
          <p>
            Setup, subscriptions, domains, access, maintenance, health and SSL
            for every store.
          </p>
        </div>
        <div className="store-overview-header-actions">
          <button
            type="button"
            className="so-btn"
            onClick={checkAllStoreHealth}
            disabled={checkingAllStores || refreshing}
          >
            <Activity size={16} className={checkingAllStores ? "spin" : ""} />
            {checkingAllStores ? "Checking stores..." : "Check all stores"}
          </button>
          <button
            type="button"
            className="so-btn so-btn-primary"
            onClick={() => loadStores(true)}
            disabled={refreshing || checkingAllStores}
          >
            <RefreshCw size={16} className={refreshing ? "spin" : ""} />
            {refreshing ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </header>

      {healthCheckSummary && (
        <div className="store-overview-success" role="status">
          {healthCheckSummary}
        </div>
      )}

      {error && (
        <div className="store-overview-error" role="alert">
          <span>{error}</span>
          <button type="button" onClick={() => loadStores()}>
            Try again
          </button>
        </div>
      )}

      <div className="store-overview-stats">
        <article className="store-overview-stat is-blue">
          <span className="so-stat-icon">
            <Store size={20} aria-hidden="true" />
          </span>
          <div>
            <span className="so-stat-label">Stores</span>
            <strong>{stores.length}</strong>
            <small>{activeStoreCount} active</small>
          </div>
        </article>
        <article className="store-overview-stat is-green">
          <span className="so-stat-icon">
            <Globe size={20} aria-hidden="true" />
          </span>
          <div>
            <span className="so-stat-label">Online</span>
            <strong>{onlineStoreCount}</strong>
            <small>of {stores.length} stores</small>
          </div>
        </article>
        <article className="store-overview-stat is-violet">
          <span className="so-stat-icon">
            <Users size={20} aria-hidden="true" />
          </span>
          <div>
            <span className="so-stat-label">Tenant users</span>
            <strong>{userCount}</strong>
            <small>across all stores</small>
          </div>
        </article>
        <article className="store-overview-stat is-red">
          <span className="so-stat-icon">
            <ShieldAlert size={20} aria-hidden="true" />
          </span>
          <div>
            <span className="so-stat-label">SSL alerts</span>
            <strong>{sslAlertCount}</strong>
            <small>
              {sslExpiredCount} expired, {sslExpiringCount} expiring soon
            </small>
          </div>
        </article>
      </div>

      <section className="store-overview-card">
        <div className="store-overview-filters">
          <label className="so-search">
            <Search size={16} aria-hidden="true" />
            <input
              type="search"
              placeholder="Search by store, domain, email or user"
              value={filters.search}
              onChange={(e) => setFilter("search", e.target.value)}
              aria-label="Search stores"
            />
          </label>

          <div className="so-filter-grid">
            <label className="so-field">
              <span>Status</span>
              <select
                value={filters.status}
                onChange={(e) => setFilter("status", e.target.value)}
              >
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </label>

            <label className="so-field">
              <span>Setup</span>
              <select
                value={filters.setup}
                onChange={(e) => setFilter("setup", e.target.value)}
              >
                <option value="all">All</option>
                {options.setup.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </label>

            <label className="so-field">
              <span>Plan</span>
              <select
                value={filters.plan}
                onChange={(e) => setFilter("plan", e.target.value)}
              >
                <option value="all">All</option>
                {options.plan.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </label>

            <label className="so-field">
              <span>Subscription</span>
              <select
                value={filters.subscription}
                onChange={(e) => setFilter("subscription", e.target.value)}
              >
                <option value="all">All</option>
                {options.subscription.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </label>

            <label className="so-field">
              <span>Activity</span>
              <select
                value={filters.activity}
                onChange={(e) => setFilter("activity", e.target.value)}
              >
                {Object.entries(ACTIVITY_RANGES).map(([key, { label }]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
            </label>

            <label className="so-field">
              <span>SSL expiry</span>
              <select
                value={filters.ssl}
                onChange={(e) => setFilter("ssl", e.target.value)}
              >
                {Object.entries(CERTIFICATE_FILTERS).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>

        <div className="store-overview-toolbar">
          <div className="so-toolbar-count">
            <span aria-live="polite">
              Showing <strong>{filteredStores.length}</strong> of{" "}
              {stores.length} stores
            </span>
            {activeFilterCount > 0 && (
              <button type="button" className="so-link" onClick={resetFilters}>
                <X size={14} aria-hidden="true" />
                Clear {activeFilterCount} filter
                {activeFilterCount > 1 ? "s" : ""}
              </button>
            )}
          </div>
          <div className="so-toolbar-controls">
            <label className="so-inline-select">
              <span>Sort by</span>
              <select
                value={filters.sort}
                onChange={(e) => setFilter("sort", e.target.value)}
              >
                {Object.entries(SORTS).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <div
              className="store-overview-view-toggle"
              role="group"
              aria-label="Store layout"
            >
              <button
                type="button"
                className={viewMode === "table" ? "is-selected" : ""}
                onClick={() => setViewMode("table")}
                aria-pressed={viewMode === "table"}
                aria-label="Table view"
                title="Table view"
              >
                <List size={16} aria-hidden="true" />
              </button>
              <button
                type="button"
                className={viewMode === "grid" ? "is-selected" : ""}
                onClick={() => setViewMode("grid")}
                aria-pressed={viewMode === "grid"}
                aria-label="Grid view"
                title="Grid view"
              >
                <LayoutGrid size={16} aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>

        {filteredStores.length === 0 ? (
          <div className="store-overview-empty">
            <Store size={28} aria-hidden="true" />
            <p>
              {error
                ? "Store data is unavailable."
                : stores.length === 0
                  ? "No stores found."
                  : "No stores match these filters."}
            </p>
            {activeFilterCount > 0 && (
              <button type="button" className="so-btn" onClick={resetFilters}>
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <>
            {viewMode === "grid" ? (
              <div className="store-overview-grid">
                {pageStores.map((store) => {
                  const m = store.monitoring;
                  return (
                    <article
                      key={store.tenantId}
                      className="store-overview-grid-card"
                      role="button"
                      tabIndex={0}
                      aria-label={`View details for ${storeName(store) || "store"}`}
                      onClick={() => openStore(store)}
                      onKeyDown={(event) => handleStoreRowKeyDown(event, store)}
                    >
                      <div className="so-grid-card-header">
                        <Avatar store={store} />
                        <div>
                          <strong>{storeName(store) || "Unnamed store"}</strong>
                          <small>{storeDomain(store) || "No domain"}</small>
                        </div>
                        <StatusBadge active={store.isActive} />
                      </div>
                      <div className="so-grid-card-indicators">
                        <HealthPill monitoring={m} />
                        <SslPill monitoring={m} />
                      </div>
                      <p
                        className={`so-grid-card-expiry is-${getCertificateTone(m)}`}
                      >
                        {getCertificateDaysLabel(m)}
                      </p>
                      <dl>
                        <div>
                          <dt>Setup</dt>
                          <dd>{store.setupStatus || "Unknown"}</dd>
                        </div>
                        <div>
                          <dt>Plan</dt>
                          <dd>{store.subscriptionPlan || "—"}</dd>
                        </div>
                        <div>
                          <dt>Maintenance</dt>
                          <dd>{store.maintenanceMode ? "On" : "Off"}</dd>
                        </div>
                        <div>
                          <dt>Users</dt>
                          <dd>
                            {Array.isArray(store.users)
                              ? store.users.length
                              : 0}
                          </dd>
                        </div>
                      </dl>
                      <span className="so-grid-card-link">
                        View details
                        <ChevronRight size={14} aria-hidden="true" />
                      </span>
                    </article>
                  );
                })}
              </div>
            ) : (
              <div className="store-overview-table-scroll">
                <table className="store-overview-table">
                  <thead>
                    <tr>
                      <th>Store</th>
                      <th>Health &amp; SSL</th>
                      <th>Status</th>
                      <th>Setup</th>
                      <th>Subscription</th>
                      <th className="is-num">Users</th>
                      <th>Last activity</th>
                      <th aria-label="Open details" />
                    </tr>
                  </thead>
                  <tbody>
                    {pageStores.map((store) => {
                      const users = Array.isArray(store.users)
                        ? store.users
                        : [];
                      const m = store.monitoring;
                      return (
                        <tr
                          key={store.tenantId}
                          tabIndex={0}
                          aria-label={`View details for ${storeName(store) || "store"}`}
                          onClick={() => openStore(store)}
                          onKeyDown={(event) =>
                            handleStoreRowKeyDown(event, store)
                          }
                        >
                          <td>
                            <div className="so-store-cell">
                              <Avatar store={store} />
                              <div>
                                <strong>
                                  {storeName(store) || "Unnamed store"}
                                </strong>
                                <small>
                                  {storeDomain(store) || store.tenantCode}
                                </small>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className="so-pill-stack">
                              <HealthPill monitoring={m} />
                              <SslPill monitoring={m} />
                              <small
                                className={`so-expiry is-${getCertificateTone(m)}`}
                              >
                                {getCertificateDaysLabel(m)}
                              </small>
                            </div>
                          </td>
                          <td>
                            <StatusBadge active={store.isActive} />
                            {store.maintenanceMode && (
                              <small className="so-maint-flag">
                                Maintenance on
                              </small>
                            )}
                          </td>
                          <td>{store.setupStatus || "Unknown"}</td>
                          <td>
                            <strong className="so-plan">
                              {store.subscriptionPlan || "—"}
                            </strong>
                            <small>
                              {store.subscriptionStatus || "Unknown"}
                            </small>
                          </td>
                          <td className="is-num">{users.length}</td>
                          <td className="so-date-cell">
                            {formatDate(store.lastActivityAt)}
                          </td>
                          <td className="so-chevron-cell">
                            <ChevronRight size={18} aria-hidden="true" />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <footer className="store-overview-pagination">
              <span>
                {pageStart + 1}–
                {Math.min(pageStart + PAGE_SIZE, filteredStores.length)} of{" "}
                {filteredStores.length}
              </span>
              <div>
                <button
                  type="button"
                  className="so-icon-btn"
                  onClick={() => setPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                >
                  <ChevronLeft size={16} />
                </button>
                <span>
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  type="button"
                  className="so-icon-btn"
                  onClick={() => setPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  aria-label="Next page"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </footer>
          </>
        )}
      </section>

      <Modal
        isOpen={Boolean(selectedStore)}
        onClose={closeStore}
        title={selectedStore?.businessName || "Store details"}
        size="xl"
        className="store-overview-modal"
      >
        {selectedStore && (
          <div className="store-overview-details">
            <div className="so-hero">
              <Avatar store={selectedStore} large />
              <div className="so-hero-text">
                <strong>{storeName(selectedStore) || "Unnamed store"}</strong>
                <span>{selectedDomain || "No domain configured"}</span>
              </div>
              <div className="so-hero-pills">
                <StatusBadge active={selectedStore.isActive} />
                <HealthPill monitoring={monitoring} />
                <SslPill monitoring={monitoring} />
                {selectedStore.maintenanceMode && (
                  <span className="so-pill is-warn">
                    <Wrench size={13} aria-hidden="true" />
                    Maintenance
                  </span>
                )}
              </div>
            </div>

            {detailsError && (
              <div className="store-overview-error" role="alert">
                {detailsError}
              </div>
            )}
            {detailsMessage && (
              <div className="store-overview-success" role="status">
                {detailsMessage}
              </div>
            )}

            <div className="so-tabs" role="tablist" aria-label="Store sections">
              {MODAL_TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  className={activeTab === tab.id ? "is-active" : ""}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                  {tab.id === "users" && (
                    <span className="so-tab-count">{selectedUsers.length}</span>
                  )}
                </button>
              ))}
            </div>

            <div role="tabpanel">
              {activeTab === "overview" && renderOverviewTab()}
              {activeTab === "billing" && renderBillingTab()}
              {activeTab === "domain" && renderDomainTab()}
              {activeTab === "users" && renderUsersTab()}
              {activeTab === "history" && renderHistoryTab()}
            </div>

            <p className="so-audit-note">
              <LockKeyhole size={14} aria-hidden="true" />
              Store-control changes are recorded in the administrator audit log.
            </p>
          </div>
        )}
      </Modal>
      <ConfirmModal
        isOpen={Boolean(pendingConfirmation)}
        onClose={() => setPendingConfirmation(null)}
        onConfirm={confirmPendingAction}
        title={pendingConfirmation?.title}
        message={pendingConfirmation?.message}
        confirmText={pendingConfirmation?.confirmText}
        variant={pendingConfirmation?.variant}
      />
    </div>
  );
};

export default StoreOverview;
